/**
 * 模擬試験 (/interview-questions/coding/coding-test/mock) の決まりと記録。
 *
 * 60 分で 2 問 (やさしい 1 問とふつう 1 問)。1 問ずつ提出して、隠れたテストを全部通ったかで数える。
 * 時計は始めた時刻を localStorage に残し、再読み込みしても続く。時間が来たら、それまでの提出で数えて結果へ進む。
 * 記録は手元のブラウザ (localStorage) だけ。サーバーには送らない。端末の時刻を変えればずらせるが、練習なので受け入れる (設計 5-3)。
 * 読み書きは try/catch で包み、使えない端末 (保存の禁止) では記録が残らないだけで、頁は動く
 */
import type { CodingTestTier } from "@/lib/coding-test";
import type { JudgeLanguage } from "@/lib/stdio-judge/types";

export const MOCK_MINUTES = 60;
export const MOCK_DURATION_MS = MOCK_MINUTES * 60 * 1000;
/** 残りがこれ以下で色を変える (注意、急ぐ) */
export const MOCK_WARN_MS = 10 * 60 * 1000;
export const MOCK_URGENT_MS = 60 * 1000;
export const MOCK_STORAGE_KEY = "coding-test-mock-v1";
const HISTORY_LIMIT = 20;

export type MockProblemRef = { slug: string; tier: CodingTestTier; estimatedMinutes: number; order: number };

/** 組。やさしい 1 問とふつう 1 問 (この順に出す) */
export type MockSet = { id: string; slugs: [string, string] };

/**
 * やさしい (A) とふつう (B) の全部の組み合わせ。id は 2 つの slug から決まるので、問題を足しても今までの組の id は変わらない。
 * 並びは、やさしい問題の順、その中でふつうの問題の順
 */
export function buildMockSets(problems: MockProblemRef[]): MockSet[] {
  const byOrder = [...problems].sort((a, b) => a.order - b.order);
  const easy = byOrder.filter((p) => p.tier === "A");
  const medium = byOrder.filter((p) => p.tier === "B");
  return easy.flatMap((a) => medium.map((b): MockSet => ({ id: `${a.slug}+${b.slug}`, slugs: [a.slug, b.slug] })));
}

export type MockSubmission = { passed: number; total: number; language: JudgeLanguage; at: number };
export type MockEnd = "finished" | "time-up" | "quit";

export type MockRun = {
  setId: string;
  slugs: string[];
  startedAt: number;
  durationMs: number;
  /** slug ごとの提出 (古い順) */
  submissions: Record<string, MockSubmission[]>;
  finishedAt: number | null;
  endedBy: MockEnd | null;
};

export type MockProblemResult = {
  slug: string;
  /** 一度でも全部のテストに通した */
  solved: boolean;
  /** 全部通した提出までに、始めてからかかった時間 (ミリ秒)。通していなければ null */
  solvedAfterMs: number | null;
  submissions: number;
  /** 最後の提出で通ったテストの数と全部の数 (提出が無ければ null) */
  last: { passed: number; total: number } | null;
  /** どの提出でも、通ったテストの数が最も多かった提出 */
  best: { passed: number; total: number } | null;
};

export type MockSummary = {
  setId: string;
  startedAt: number;
  finishedAt: number;
  endedBy: MockEnd;
  elapsedMs: number;
  solvedCount: number;
  problems: MockProblemResult[];
};

export type MockState = { run: MockRun | null; history: MockSummary[] };

export type MockStorage = Pick<Storage, "getItem" | "setItem">;

function browserStorage(): MockStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

function parseSubmission(value: unknown): MockSubmission | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (!isNumber(v.passed) || !isNumber(v.total) || !isNumber(v.at) || (v.language !== "python" && v.language !== "javascript")) return null;
  return { passed: v.passed, total: v.total, language: v.language, at: v.at };
}

function parseRun(value: unknown): MockRun | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.setId !== "string" || !Array.isArray(v.slugs) || !v.slugs.every((s) => typeof s === "string")) return null;
  if (!isNumber(v.startedAt) || !isNumber(v.durationMs) || v.durationMs <= 0) return null;
  const submissions: Record<string, MockSubmission[]> = {};
  if (typeof v.submissions === "object" && v.submissions !== null) {
    for (const [slug, list] of Object.entries(v.submissions)) {
      if (Array.isArray(list)) submissions[slug] = list.flatMap((item) => parseSubmission(item) ?? []);
    }
  }
  const endedBy = v.endedBy === "finished" || v.endedBy === "time-up" || v.endedBy === "quit" ? v.endedBy : null;
  return {
    setId: v.setId,
    slugs: v.slugs as string[],
    startedAt: v.startedAt,
    durationMs: v.durationMs,
    submissions,
    finishedAt: isNumber(v.finishedAt) ? v.finishedAt : null,
    endedBy,
  };
}

function parseSummary(value: unknown): MockSummary | null {
  if (typeof value !== "object" || value === null) return null;
  const v = value as Record<string, unknown>;
  if (typeof v.setId !== "string" || !isNumber(v.startedAt) || !isNumber(v.finishedAt) || !isNumber(v.elapsedMs) || !isNumber(v.solvedCount)) return null;
  if (v.endedBy !== "finished" && v.endedBy !== "time-up" && v.endedBy !== "quit") return null;
  if (!Array.isArray(v.problems)) return null;
  return value as MockSummary;
}

export function readMockState(storage: MockStorage | null = browserStorage()): MockState {
  const empty: MockState = { run: null, history: [] };
  if (!storage) return empty;
  try {
    const parsed: unknown = JSON.parse(storage.getItem(MOCK_STORAGE_KEY) ?? "null");
    if (typeof parsed !== "object" || parsed === null) return empty;
    const v = parsed as Record<string, unknown>;
    return {
      run: parseRun(v.run),
      history: Array.isArray(v.history) ? v.history.flatMap((item) => parseSummary(item) ?? []) : [],
    };
  } catch {
    return empty;
  }
}

function write(storage: MockStorage | null, state: MockState): boolean {
  if (!storage) return false;
  try {
    storage.setItem(MOCK_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export function remainingMs(run: MockRun, now: number): number {
  return Math.max(0, run.startedAt + run.durationMs - now);
}

export function isRunning(run: MockRun | null, now: number): run is MockRun {
  return run !== null && run.finishedAt === null && remainingMs(run, now) > 0;
}

export function summarize(run: MockRun, finishedAt: number, endedBy: MockEnd): MockSummary {
  const problems = run.slugs.map((slug): MockProblemResult => {
    const list = run.submissions[slug] ?? [];
    const full = list.find((s) => s.total > 0 && s.passed === s.total);
    const last = list[list.length - 1];
    const best = list.reduce<MockSubmission | null>((a, b) => (a === null || b.passed > a.passed ? b : a), null);
    return {
      slug,
      solved: Boolean(full),
      solvedAfterMs: full ? Math.max(0, full.at - run.startedAt) : null,
      submissions: list.length,
      last: last ? { passed: last.passed, total: last.total } : null,
      best: best ? { passed: best.passed, total: best.total } : null,
    };
  });
  return {
    setId: run.setId,
    startedAt: run.startedAt,
    finishedAt,
    endedBy,
    elapsedMs: Math.max(0, Math.min(finishedAt, run.startedAt + run.durationMs) - run.startedAt),
    solvedCount: problems.filter((p) => p.solved).length,
    problems,
  };
}

/** 始める。前の記録は残し、進行中の run だけを作り直す */
export function startMockRun(set: MockSet, now: number, storage: MockStorage | null = browserStorage()): MockRun {
  const run: MockRun = { setId: set.id, slugs: [...set.slugs], startedAt: now, durationMs: MOCK_DURATION_MS, submissions: {}, finishedAt: null, endedBy: null };
  write(storage, { run, history: readMockState(storage).history });
  return run;
}

/** 提出の結果を残す。終わった後と、時間が来たあとの提出は数えない (残さず null) */
export function recordMockSubmission(slug: string, submission: MockSubmission, storage: MockStorage | null = browserStorage()): MockRun | null {
  const state = readMockState(storage);
  const run = state.run;
  if (!run || !run.slugs.includes(slug) || !isRunning(run, submission.at)) return null;
  const next: MockRun = { ...run, submissions: { ...run.submissions, [slug]: [...(run.submissions[slug] ?? []), submission] } };
  write(storage, { run: next, history: state.history });
  return next;
}

/** 終える。run を残したまま結果にし、記録 (history) に足す。すでに終わっていれば何もしない */
export function finishMockRun(now: number, endedBy: MockEnd, storage: MockStorage | null = browserStorage()): MockRun | null {
  const state = readMockState(storage);
  const run = state.run;
  if (!run) return null;
  if (run.finishedAt !== null) return run;
  const finishedAt = Math.min(now, run.startedAt + run.durationMs);
  const done: MockRun = { ...run, finishedAt, endedBy };
  const history = [summarize(done, finishedAt, endedBy), ...state.history].slice(0, HISTORY_LIMIT);
  write(storage, { run: done, history });
  return done;
}

/** 時間が来ていたら、時間切れで終える (開いたとき、時計の刻みごとに呼ぶ) */
export function settleMockRun(now: number, storage: MockStorage | null = browserStorage()): MockRun | null {
  const { run } = readMockState(storage);
  if (run && run.finishedAt === null && remainingMs(run, now) === 0) return finishMockRun(now, "time-up", storage);
  return run;
}

/** 結果の画面を閉じて、次の組を選べるようにする (記録 history は残す) */
export function clearMockRun(storage: MockStorage | null = browserStorage()) {
  write(storage, { run: null, history: readMockState(storage).history });
}

/** 「12 分 05 秒」「1 時間 00 分」の形に */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h} 時間 ${String(m).padStart(2, "0")} 分`;
  return `${m} 分 ${String(s).padStart(2, "0")} 秒`;
}

/** 時計の「59:59」の形 */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/** 公開されているスキルチェックのランクの目安 (調べた数。この頁の採点とは別の物差し) */
export const RANK_GUIDE = [
  { rank: "C", top: "上位約 60%", median: "約 11 分" },
  { rank: "B", top: "上位約 30%", median: "約 31 分" },
  { rank: "A", top: "上位約 10%", median: "約 43 分" },
] as const;
